import { combineReducers, configureStore, createAction, ThunkAction } from '@reduxjs/toolkit'
import { createWrapper } from 'next-redux-wrapper'
import { Action, PreloadedState } from 'redux'
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  purgeStoredState,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import persistStorage from 'redux-persist/lib/storage'
import storageSession from 'redux-persist/lib/storage/session'

import learningContextReducer from '~/aioracle/slices/learning-context.slice'
import { mathVaultActivityApi } from '~/math-vault/apis/mathVaultActivityApi'
import onboardingReducer from '~/onboarding/redux/onboarding.slice'
import onboardingFlowReducer from '~/onboarding-flow/slices/onboardingFlow.slice'
import { OnboardingExperimentVariant } from '~/onboarding-flow/consts/experiment.const'
import statisticsReducer from '~/statistics/redux/statistics.slice'
import settingsReducer from '~/settings-scenario/redux/settings.slice'
import loginReducer from '~/auth/slices/login.slice'
import recaptchaReducer from '~/auth/slices/recaptcha.slice'
import { bootcampSplitApi } from '~/bootcamp/apis/split.api'
import bootcampActivityReducer from '~/bootcamp/slices/bootcamp-activity.slice'
import bootcampPageReducer from '~/bootcamp/slices/bootcamp-page.slice'
import bootcampQuizReducer from '~/bootcamp/slices/bootcamp-quiz.slice'
import bootcampVideoReducer from '~/bootcamp/slices/bootcamp-video.slice'
import { studytipsApi } from '~/study-tips/apis/studyTips.api'
import authReducer, { logout } from '~/auth/slices/auth.slice'
import schoolTeacherReducer from '~/classroom/slices/schoolTeacher.slice'
import homebaseQuizReducer from '~/redux/slices/homebaseQuiz.slice'
import gemShopReducer from '~/redux/slices/gem-shop'
import { dragonApi } from '~/redux/apis/dragon-api'
import { gemShopApi } from '~/redux/apis/gem-shop-api'
import employeeReducer from '~/employee/slices/employee.slice'
import vccDataReducer from '~/bootcamp/slices/vcc-data.slice'
import donationReducer from '~/donation/redux/donation.slice'
import { rewardApi } from '~/redux/apis/reward-api'
import { notificationApi } from '~/redux/apis/notification-api'
import curriculumReducer from '~/curriculum/slice/curriculum.slice'
import testPrepReducer from '~/tests/slice/testPrep.slice'
import persistCurriculumReducer from '~/curriculum/slice/persistCurriculum.slice'
import { curriculumUserStatApi } from '~/curriculum/apis/get-curriculum-user-stats.api'
import { IUser } from '~/types/user-info-types'
import { StripeSubscriptionStatus } from '~/types/stripe-subscription'
import familyAccountReducer from '~/familyAccount/redux/familyAccount.slice'

import redirectionReducer from '~/curriculum/slice/redirection.slice'

import gamificationReducer from '../gamification/slices/gamification.slice'
import userFeedbackReducer from '../user-feedback/redux/user-feedback'

import linje5 from '../campaigns/linje5/slice/linje5.slice'

import { staticPageApi } from './apis/static-page-api'
import { activityApi } from './apis/activity-api'
import { adminApi } from './apis/admin-api'
import { blogApi } from './apis/blog.api'
import { homApi } from './apis/hom'
import { homInfoApi } from './apis/hom-info-api'
import { mediaApi } from './apis/media.api'
import { multipleChoiceTestApi } from './apis/multiple-choice-test-api'
import { questApi } from './apis/quest-api'
import adminScheduleReducer from './slices/admin-schedule'
import subscriptionReducer from './slices/subscription.slice'
import avatarReducer from './slices/avatar'
import cartReducer from './slices/cart'
import chatReducer from './slices/chat'
import chatAiReducer from './slices/chat-ai'
import dashboardReducer from './slices/dashboard'
import checkoutReducer from './slices/checkout'
import dropdownMenuReducer from './slices/dropdown-menu'
import homebaseReducer from './slices/homebase'
import mathVaultReducer from './slices/math-vault'
import menuReducer from './slices/menu'
import modalsReducer from './slices/modals'
import multipleChoiceTestReducer from './slices/multiple-choice'
import orderReducer from './slices/order'
import petStateReducer from './slices/pet-state'
import scheduleSessionReducer from './slices/schedule-session'
import searchReducer from './slices/search'
import paywallReducer from './slices/paywall'
import soundControlReducer from './slices/sound-control'
import storyCardReducer from './slices/story-card'
import adminSubscriptionReducer from './slices/admin-subscription'
import theoryReducer from './slices/theory'
import uiRecucer from './slices/ui'
import userReducer from './slices/user'
import { craneGameApi } from './apis/crane-game-api'
import questReducer from './slices/quest'
import { collectionApi } from './apis/collection-api'
import checklistReducer from './slices/checklist'
import mindOverlayReducer from './slices/mind-overlay'
import { listenerMiddleware } from './listenerMiddleware'

const chatAiPersistConfig = {
  key: 'chatAi',
  storage: storageSession, // only chatAiReducer will be persisted
  // availabilityCount, hideBottomMenu, hasInlineOracle, isChatAiOpen: route-scoped, so a rehydrated count never reaches 0.
  // isLoading, stopMessage, currentMessage, errorMessageType: one turn's state, which a reload ends; kept, a
  // reload mid-answer would leave the chat locked and the AI page unable to send. freshNudgeId: a reload would
  // replay the nudge animation.
  blacklist: [
    'availabilityCount',
    'hideBottomMenu',
    'hasInlineOracle',
    'isChatAiOpen',
    'activitySheetMode',
    'isLoading',
    'stopMessage',
    'currentMessage',
    'errorMessageType',
    'freshNudgeId',
  ],
}
const sessionPersistConfig = {
  key: 'session',
  storage: storageSession,
}
const onboardingPersistConfig = {
  key: 'onboarding',
  storage: storageSession,
}
const onboardingFlowPersistConfig = {
  key: 'onboardingFlow',
  storage: storageSession,
}
const subscriptionPersistConfig = {
  key: 'subscription',
  storage: storageSession, // only subscriptionProduct survives the post-payment reload
  whitelist: ['subscriptionProduct'],
}

const curriculumStateReconciler = (inboundState: any, _originalState: any, reducedState: any) => {
  if (!inboundState) {
    return reducedState
  }

  // Preserve SSR-hydrated curriculum values for these keys.
  return {
    ...reducedState,
    ...inboundState,
    gradeListByCountry: reducedState.gradeListByCountry,
    gradeContent: reducedState.gradeContent,
    gradeOverviewFromSanity: reducedState.gradeOverviewFromSanity,
    curriculumSubjectContent: reducedState.curriculumSubjectContent,
    // Transient UI state: the whole slice is persisted to sessionStorage, so without this the footer
    // curriculum dropdown re-opens itself on every page load for the rest of the session. The footer is
    // always on screen, which makes that very visible. NOTE: menuCurriculumDropdownExpanded and
    // gradePageCurriculumDropdownExpanded have the same latent issue but are left alone here.
    footerCurriculumDropdownExpanded: reducedState.footerCurriculumDropdownExpanded,
  }
}

const curriculumPersistConfig = {
  key: 'curriculum',
  storage: storageSession,
  stateReconciler: curriculumStateReconciler,
}

const rootReducer = combineReducers({
  user: userReducer,
  adminSchedule: adminScheduleReducer,
  auth: authReducer,
  schoolTeacher: schoolTeacherReducer,
  avatar: avatarReducer,
  subscription: persistReducer(subscriptionPersistConfig, subscriptionReducer),
  bootcampActivity: bootcampActivityReducer,
  bootcampPage: bootcampPageReducer,
  bootcampVideo: bootcampVideoReducer,
  vcc: vccDataReducer,
  bootcampQuiz: bootcampQuizReducer,
  cart: cartReducer,
  chat: chatReducer,
  chatAi: persistReducer(chatAiPersistConfig, chatAiReducer), // user subtree saved in session storage
  checkout: checkoutReducer,
  curriculum: persistReducer(curriculumPersistConfig, curriculumReducer),
  dashboard: dashboardReducer,
  dropdownMenu: dropdownMenuReducer,
  employee: employeeReducer,
  gamification: gamificationReducer,
  gemShop: gemShopReducer,
  homebase: homebaseReducer,
  familyAccount: familyAccountReducer,
  // Deliberately unpersisted: a persisted context would report a question the student closed hours ago.
  learningContext: learningContextReducer,
  login: loginReducer,
  recaptcha: recaptchaReducer,
  homebaseQuiz: homebaseQuizReducer,
  mathVault: mathVaultReducer,
  menu: menuReducer,
  multipleChoiceTest: multipleChoiceTestReducer,
  onboarding: persistReducer(onboardingPersistConfig, onboardingReducer),
  onboardingFlow: persistReducer(onboardingFlowPersistConfig, onboardingFlowReducer),
  petState: petStateReducer,
  quest: questReducer,
  order: orderReducer,
  scheduleSession: scheduleSessionReducer,
  search: searchReducer,
  paywall: persistReducer(sessionPersistConfig, paywallReducer),
  statistics: statisticsReducer,
  storyCard: storyCardReducer,
  adminSubscription: adminSubscriptionReducer,
  theory: theoryReducer,
  ui: uiRecucer,
  modals: modalsReducer,
  donation: donationReducer,
  redirection: redirectionReducer,
  userFeedback: userFeedbackReducer,
  linje5,

  [notificationApi.reducerPath]: notificationApi.reducer,
  [homApi.reducerPath]: homApi.reducer,
  soundControl: soundControlReducer,
  [blogApi.reducerPath]: blogApi.reducer,
  [staticPageApi.reducerPath]: staticPageApi.reducer,
  [activityApi.reducerPath]: activityApi.reducer,
  [bootcampSplitApi.reducerPath]: bootcampSplitApi.reducer,
  [craneGameApi.reducerPath]: craneGameApi.reducer,
  [rewardApi.reducerPath]: rewardApi.reducer,
  [dragonApi.reducerPath]: dragonApi.reducer,
  [gemShopApi.reducerPath]: gemShopApi.reducer,
  [homInfoApi.reducerPath]: homInfoApi.reducer,
  [mathVaultActivityApi.reducerPath]: mathVaultActivityApi.reducer,
  [mediaApi.reducerPath]: mediaApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [multipleChoiceTestApi.reducerPath]: multipleChoiceTestApi.reducer,
  [questApi.reducerPath]: questApi.reducer,
  [studytipsApi.reducerPath]: studytipsApi.reducer,
  settings: settingsReducer,
  [collectionApi.reducerPath]: collectionApi.reducer,
  [curriculumUserStatApi.reducerPath]: curriculumUserStatApi.reducer,
  checklist: checklistReducer,
  mindOverlay: mindOverlayReducer,
  testPrep: testPrepReducer,
  persistCurriculum: persistCurriculumReducer,
})

/**
 * The school edition's "a new person is at this device": sign-out, and a single sign-on callback.
 *
 * `logout` resets the slices that hold a person (`schoolTeacher`, `chat`, `chatAi`, `checklist`, ...),
 * but five PERSISTED slices do not handle it, and purging storage does not clear memory: the next
 * dispatch writes them straight back. So this is `logout` plus those five, returned to their initial
 * state. Only school hooks dispatch it, so the b2c logout is unchanged.
 *
 * Deliberately not every slice: `curriculum`, `ui` and the other page-content slices are filled by the
 * server render, and resetting them would blank the page until the next navigation. `soundControl` is
 * a device preference.
 */
export const schoolSessionReset = createAction('school/sessionReset')

const withSchoolSessionReset: typeof rootReducer = (state, action) => {
  if (!state || !schoolSessionReset.match(action)) return rootReducer(state, action)

  const {
    testPrep: _testPrep,
    order: _order,
    dashboard: _dashboard,
    donation: _donation,
    persistCurriculum: _persistCurriculum,
    ...kept
  } = rootReducer(state, logout())

  // The five omitted keys arrive undefined, so their reducers start from initial state.
  return rootReducer(kept as RootState, action)
}

/** Every RTK Query cache: which ones hold a person's data changes as features are added. */
export const resetAllApiState = (dispatch: AppDispatch): void => {
  const caches = [
    notificationApi,
    homApi,
    blogApi,
    staticPageApi,
    activityApi,
    bootcampSplitApi,
    craneGameApi,
    rewardApi,
    dragonApi,
    gemShopApi,
    homInfoApi,
    mathVaultActivityApi,
    mediaApi,
    adminApi,
    multipleChoiceTestApi,
    questApi,
    studytipsApi,
    collectionApi,
    curriculumUserStatApi,
  ]

  caches.forEach((api) => dispatch(api.util.resetApiState()))
}

type Reducer = typeof rootReducer

const setupStore = (reducer: Reducer, preloadedState?: PreloadedState<RootState>, options = {}, devTools = false) => {
  return configureStore({
    reducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware(options).concat(
        homApi.middleware,
        staticPageApi.middleware,
        blogApi.middleware,
        activityApi.middleware,
        bootcampSplitApi.middleware,
        craneGameApi.middleware,
        rewardApi.middleware,
        dragonApi.middleware,
        gemShopApi.middleware,
        homInfoApi.middleware,
        mathVaultActivityApi.middleware,
        mediaApi.middleware,
        adminApi.middleware,
        multipleChoiceTestApi.middleware,
        questApi.middleware,
        studytipsApi.middleware,
        collectionApi.middleware,
        notificationApi.middleware,
        curriculumUserStatApi.middleware,
        listenerMiddleware.middleware,
      ),
    devTools,
  })
}

export let storeInstance: AppStore

// The parent panel's PIN unlock is for one visit: never saved, and an old saved `true` is not restored
interface FamilyAdminLock {
  isFamilyAdminAuthenticated?: boolean
}
const lockFamilyAdmin = (state: FamilyAdminLock): FamilyAdminLock => ({ ...state, isFamilyAdminAuthenticated: false })
export const familyAdminLockTransform = createTransform<FamilyAdminLock, FamilyAdminLock>(
  lockFamilyAdmin,
  lockFamilyAdmin,
  {
    whitelist: ['familyAccount'],
  },
)

export const persistConfig = {
  key: 'root-v4',
  version: 1,
  storage: persistStorage,
  whitelist: [
    'cart',
    'chat',
    'testPrep',
    'soundControl',
    'order',
    'dashboard',
    'donation',
    'checklist',
    'familyAccount',
    'persistCurriculum',
    // The teacher panel's identity. The access token already survives a reload in local storage, so
    // this has to as well — otherwise a refresh leaves a signed-in teacher with no avatar. Display
    // data only; nothing is authorised from it. See `schoolTeacher.slice.ts`.
    'schoolTeacher',
  ],
  transforms: [familyAdminLockTransform],
}

export const makeStore = (preloadedState?: PreloadedState<RootState>): AppStore => {
  const isServer = typeof process.env.ENVIRONMENT !== 'undefined'
  const devTools = process.env.NODE_ENV === 'development'

  if (isServer) return setupStore(withSchoolSessionReset, preloadedState, undefined, devTools)

  const persistedReducer = persistReducer(persistConfig, withSchoolSessionReset)

  const store = setupStore(
    persistedReducer,
    preloadedState,
    {
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    },
    devTools,
  )

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  store.__persistor = persistStore(store) // Nasty hack
  storeInstance = store

  return store
}

export type AppStore = ReturnType<typeof setupStore>
export type RootState = ReturnType<typeof rootReducer>
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action>
export type AppDispatch = typeof storeInstance.dispatch

export const wrapper = createWrapper<AppStore>(() => makeStore(), {
  serializeState: (state) => JSON.stringify(state),
  deserializeState: (state) => JSON.parse(state),
})

export const getUserSubscriptionStatus = (): StripeSubscriptionStatus | undefined => {
  const subscriptions = storeInstance?.getState().user.current?.stripeSubscription
  const activeSubscription = subscriptions?.find((i) => i.subscriptionStatus === 'active')
  if (activeSubscription) {
    return activeSubscription.subscriptionStatus
  }

  return subscriptions?.[0]?.subscriptionStatus
}

// Read at log time rather than cached in the analytics singleton: /onboarding-flow/signup and
// /payment never mount the funnel's entry component, so a reload or deep link there would
// otherwise log the arm as undefined while the slice holds it.
export const getOnboardingArm = (): OnboardingExperimentVariant | undefined =>
  storeInstance?.getState().onboardingFlow?.arm

export const getStoreUser = (): IUser | null => {
  return storeInstance?.getState().user.current
}

export const purgePersistedState = (): void => {
  purgeStoredState(persistConfig)
}
